<script setup>
import {usePortalContentAndDesignStore} from "../../../../../../stores/partners/portalContentAndDesignStore";
import {computed, ref} from "vue";
import EditableTextField from "../../../../portal-editable-fields/EditableTextField.vue";
import EditableResourceTextEditorField
    from "../../../../portal-editable-fields/resources/EditableResourceTextEditorField.vue";
import {isCustomColor, hexToRgbString} from "../../../../../../helpers/common";

const props = defineProps({
    resource: {
        type: Object,
        required: true,
    },
})

const portalContentAndDesignStore = usePortalContentAndDesignStore();
const hideCommissionPeriod = computed(() => !!portalContentAndDesignStore.portalLayoutSettings.portal_general_hide_commission_period);

// Check program type for proper styling
const isReferralPortal = computed(() => {
    const programType = portalContentAndDesignStore.program?.program_type?.type ||
                       portalContentAndDesignStore.program?.programType?.type;
    return programType === 'refer_a_friend';
})

// Layout settings - use appropriate corners based on program type
const corners = computed(() => {
    if (isReferralPortal.value) {
        return portalContentAndDesignStore.portalLayoutSettings.referral_portal_content_layout_corners || 'rounded-xl';
    }
    return portalContentAndDesignStore.portalLayoutSettings.public_page_content_layout_corners || 'rounded-xl';
})

// Content background color
const contentBgColorClass = computed(() => {
    if (isReferralPortal.value) {
        const bgColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_background_color || 'white';
        return isCustomColor(bgColor) ? 'bg-content-custom-bg' : `bg-${bgColor}`;
    }
    const bgColor = portalContentAndDesignStore.portalLayoutSettings?.public_page_content_background_color || 'white';
    return isCustomColor(bgColor) ? 'bg-content-bg' : `bg-${bgColor}`;
})

// Content border color
const borderColorClass = computed(() => {
    if (isReferralPortal.value) {
        const borderColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_border_color || 'gray-200';
        return isCustomColor(borderColor) ? 'border-content-custom-color' : `border-${borderColor}`;
    }
    return 'border-gray-200';
})

// Helper to check if description has actual content
const hasDescription = computed(() => {
    if (!props.resource.description) return false;
    const textContent = props.resource.description.replace(/<[^>]*>/g, '').trim();
    return textContent.length > 0;
})

const hasHeader = computed(() => {
    return !!props.resource.title || hasDescription.value;
})

const alignmentClass = computed(() => {
    switch (props.resource.alignment) {
        case 'center': return 'text-center';
        case 'right': return 'text-right';
        default: return 'text-left';
    }
})

// Button/brand color for styling - use content button color for public pages
const buttonColor = computed(() => {
    if (isReferralPortal.value) {
        return portalContentAndDesignStore.portalLayoutSettings.referral_portal_content_button_color || 'teal';
    }
    return portalContentAndDesignStore.portalLayoutSettings.public_page_content_button_color || 'teal';
})

// Commission data from store (for public page, use landing page settings)
const commissionStructure = computed(() => {
    const structure = portalContentAndDesignStore.initialPortalPageData?.program_commission_structure;
    if (Array.isArray(structure)) {
        return {};
    }
    return structure || {};
})

// Extract commission rate from default commission for public landing page
const commissionRate = computed(() => {
    const defaultCommission = commissionStructure.value.default_commission;
    if (!defaultCommission?.value) return 0;
    const val = defaultCommission.value;
    if (typeof val === 'number') return val;
    const match = String(val).match(/(\d+(?:\.\d+)?)/);
    return match ? parseFloat(match[1]) : 0;
})

// Check if commission is percentage-based
const isPercentCommission = computed(() => {
    const defaultCommission = commissionStructure.value.default_commission;
    const val = defaultCommission?.value;
    if (!val) return true;
    return String(val).includes('%');
})

// Get currency symbol from commission structure (program settings) or fallback to resource config
const currencySymbol = computed(() => {
    return commissionStructure.value.currency_symbol || props.resource.currency_symbol || '$';
})

// Slider configuration
const sliderMin = computed(() => props.resource.slider_min || 1)
const sliderMax = computed(() => props.resource.slider_max || 500)
const sliderDefault = computed(() => props.resource.slider_default || 100)

// Reactive slider value
const referredCustomers = ref(sliderDefault.value)

// Pricing tiers from resource configuration
const pricingTiers = computed(() => {
    return props.resource.pricing_tiers || [];
})

// Calculation type: 'recurring' (referred customers) or 'one_time' (customer sales)
const calculationType = computed(() => props.resource.calculation_type || 'recurring')
const isRecurring = computed(() => calculationType.value === 'recurring')

// Calculate earnings for a tier
const calculateEarnings = (price) => {
    if (!price || price <= 0) return 0;
    if (isPercentCommission.value) {
        return (price * commissionRate.value / 100) * referredCustomers.value;
    }
    // Fixed commission per sale
    return commissionRate.value * referredCustomers.value;
}

const calculateEarningsPerCustomer = (price) => {
    if (!price || price <= 0) return 0;
    if (isPercentCommission.value) {
        return price * commissionRate.value / 100;
    }
    return commissionRate.value;
}

// Format currency
const formatCurrency = (amount) => {
    const value = Number(amount) || 0;
    return currencySymbol.value + value.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
}

// Slider progress percentage for styling
const sliderProgress = computed(() => {
    return ((referredCustomers.value - sliderMin.value) / (sliderMax.value - sliderMin.value)) * 100;
})

// Get the actual hex color for the slider thumb
const sliderThumbColor = computed(() => {
    const color = buttonColor.value;
    if (isCustomColor(color)) {
        return color;
    }
    const colorMap = {
        'teal': '#14b8a6',
        'teal-600': '#0d9488',
        'indigo': '#4f46e5',
        'indigo-700': '#4338ca',
        'blue': '#3b82f6',
        'blue-700': '#1d4ed8',
        'cyan': '#06b6d4',
        'cyan-700': '#0e7490',
        'black': '#111827',
    };
    return colorMap[color] || '#14b8a6';
})

// Progress bar color styling
const getProgressBarStyle = computed(() => {
    const color = buttonColor.value;
    if (isCustomColor(color)) {
        const rgb = hexToRgbString(color);
        return {
            background: `linear-gradient(to right, rgba(${rgb}, 1) 0%, rgba(${rgb}, 1) ${sliderProgress.value}%, #e5e7eb ${sliderProgress.value}%, #e5e7eb 100%)`,
            '--slider-thumb-color': color
        };
    }
    return {
        '--slider-thumb-color': sliderThumbColor.value
    };
})

const getProgressBarClass = computed(() => {
    const color = buttonColor.value;
    if (isCustomColor(color)) {
        return '';
    }
    const colorMap = {
        'teal': 'slider-teal',
        'teal-600': 'slider-teal',
        'indigo': 'slider-indigo',
        'indigo-700': 'slider-indigo',
        'blue': 'slider-blue',
        'blue-700': 'slider-blue',
        'cyan': 'slider-cyan',
        'cyan-700': 'slider-cyan',
        'black': 'slider-black',
    };
    return colorMap[color] || 'slider-teal';
})

const getTextColorClass = computed(() => {
    const color = buttonColor.value;
    if (isCustomColor(color)) {
        return 'text-custom-btn';
    }
    const colorMap = {
        'teal': 'text-teal-600',
        'teal-600': 'text-teal-600',
        'indigo': 'text-indigo-700',
        'indigo-700': 'text-indigo-700',
        'blue': 'text-blue-700',
        'blue-700': 'text-blue-700',
        'cyan': 'text-cyan-700',
        'cyan-700': 'text-cyan-700',
        'black': 'text-gray-900',
    };
    return colorMap[color] || `text-${color}`;
})

// Earnings table rows
const earningsTableRows = computed(() => {
    const rows = props.resource.earnings_table_rows || [10, 50, 100, 250, 500];
    return rows.filter(row => row <= sliderMax.value);
})

const showCalculator = computed(() => {
    return props.resource.show_calculator !== false && pricingTiers.value.length > 0;
})

const showEarningsTable = computed(() => {
    return props.resource.show_earnings_table !== false && pricingTiers.value.length > 0;
})

// Calculate table earnings
const calculateTableEarnings = (customers, price) => {
    if (!price || price <= 0) return 0;
    if (isPercentCommission.value) {
        return (price * commissionRate.value / 100) * customers;
    }
    return commissionRate.value * customers;
}

// Get page content text color
const getPageContentTextColorClass = computed(() => {
    return portalContentAndDesignStore.getPageContentTextColorClass || 'text-gray-900';
})

// Translations
const translations = computed(() => portalContentAndDesignStore.translationSettings || {});

// Default commission details for compact view
const defaultCommission = computed(() => commissionStructure.value.default_commission || {})

const commissionSummaryText = computed(() => {
    const display = defaultCommission.value.value;
    if (!display) return null;
    const period = defaultCommission.value.period;
    if (period && !hideCommissionPeriod.value) {
        const perSaleFor = translations.value.portal_translation_dashboard_per_sale_for ?? 'per sale for';
        return { value: display, label: perSaleFor, period };
    }
    return { value: display, label: translations.value.portal_translation_dashboard_per_sale ?? 'per sale', period: null };
})

const isSingleTier = computed(() => pricingTiers.value.length === 1)

const primaryEarnings = computed(() => {
    if (!isSingleTier.value) return null;
    const tier = pricingTiers.value[0];
    return {
        monthly: formatCurrency(calculateEarnings(tier.monthly_price)),
        yearly: tier.yearly_price ? formatCurrency(calculateEarnings(tier.yearly_price)) : null,
    };
})
</script>

<template>
    <div>
        <!-- Title and Description Section -->
        <div v-if="hasHeader" :class="['mb-6', alignmentClass]">
            <div class="space-y-1.5">
                <editable-text-field
                    v-if="resource.title"
                    text-type="h3"
                    :class="['text-xl md:text-2xl font-semibold', portalContentAndDesignStore.getPageContentTextColorClass]"
                    v-model="resource.title"
                />
                <editable-resource-text-editor-field
                    v-if="resource.description"
                    :class="[
                        'partnero-page-content-section text-base md:text-lg text-opacity-80 space-y-1',
                        portalContentAndDesignStore.getPageContentTextColorClass
                    ]"
                    :resource="resource"
                    resource-field="description"
                />
            </div>
        </div>

        <!-- Content Section -->
        <div>
            <!-- No commission message -->
            <div v-if="commissionRate === 0" class="text-sm text-gray-500 italic">
                {{ translations.portal_translation_earnings_no_commission ?? 'Commission structure not configured.' }}
            </div>

            <!-- No pricing tiers message -->
            <div v-else-if="pricingTiers.length === 0" class="text-sm text-gray-500 italic">
                {{ translations.portal_translation_earnings_no_tiers ?? 'No pricing tiers configured.' }}
            </div>

            <div v-else class="space-y-8">
                <!-- Earnings Calculator (Slider + Cards) -->
                <div v-if="showCalculator">
                    <!-- === Single-tier compact layout === -->
                    <div v-if="isSingleTier" class="space-y-5">
                        <div>
                            <editable-text-field
                                v-if="resource.show_earnings_heading !== false"
                                :model-value="resource.earnings_heading || (translations.portal_translation_earnings_you_could_earn ?? 'You can earn')"
                                @update:model-value="(val) => { resource.earnings_heading = val }"
                                text-type="p"
                                :custom-classes="'text-lg font-semibold ' + getPageContentTextColorClass"
                            />
                            <p class="mt-1">
                                <span :class="['text-5xl font-bold tracking-tight', getPageContentTextColorClass]">{{ primaryEarnings.monthly }}</span>
                                <span v-if="isRecurring" :class="['text-xl opacity-50', getPageContentTextColorClass]">/{{ translations.portal_translation_earnings_month ?? 'mo' }}</span>
                            </p>
                            <p v-if="pricingTiers[0].yearly_price" :class="['text-sm opacity-50 mt-1', getPageContentTextColorClass]">
                                {{ translations.portal_translation_earnings_or ?? 'or' }}
                                <span class="font-semibold">{{ primaryEarnings.yearly }}<span v-if="isRecurring">/{{ translations.portal_translation_earnings_year ?? 'yr' }}</span></span>
                                {{ translations.portal_translation_earnings_if_yearly ?? 'if all choose yearly' }}
                            </p>
                        </div>

                        <div :class="['bg-black bg-opacity-5 p-5', corners]">
                            <div class="flex items-center justify-between mb-3">
                                <div :class="['text-sm font-semibold', getPageContentTextColorClass]">
                                    {{ referredCustomers.toLocaleString() }}
                                    <span class="font-normal opacity-80">
                                        <span v-if="isRecurring">{{ translations.portal_translation_earnings_referred_customers ?? 'referred customers' }}</span>
                                        <span v-else>{{ translations.portal_translation_earnings_customer_sales ?? 'customer sales' }}</span>
                                    </span>
                                </div>
                                <div v-if="commissionSummaryText" :class="['text-sm opacity-60 shrink-0', getPageContentTextColorClass]">
                                    {{ translations.portal_translation_earnings_earn ?? 'Earn' }} <span class="font-semibold">{{ commissionSummaryText.value }}</span> {{ commissionSummaryText.label }} <span v-if="commissionSummaryText.period" class="font-semibold">{{ commissionSummaryText.period }}</span>
                                </div>
                            </div>
                            <div class="relative">
                                <input
                                    type="range"
                                    v-model.number="referredCustomers"
                                    :min="sliderMin"
                                    :max="sliderMax"
                                    :class="['earnings-slider w-full', getProgressBarClass]"
                                    :style="getProgressBarStyle"
                                />
                                <div :class="['flex justify-between text-xs opacity-60 mt-2', getPageContentTextColorClass]">
                                    <span>{{ sliderMin.toLocaleString() }}</span>
                                    <span>{{ sliderMax.toLocaleString() }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- === Multi-tier layout (2+ tiers) === -->
                    <div v-else class="space-y-5">
                        <div v-if="resource.show_earnings_heading !== false">
                            <editable-text-field
                                :model-value="resource.earnings_heading || (translations.portal_translation_earnings_you_could_earn ?? 'You can earn')"
                                @update:model-value="(val) => { resource.earnings_heading = val }"
                                text-type="span"
                                :custom-classes="'text-lg font-semibold ' + getPageContentTextColorClass"
                            />
                            <editable-text-field
                                :model-value="resource.earnings_subtitle || (translations.portal_translation_earnings_per_plan ?? 'when your referrals sign up for a plan')"
                                @update:model-value="(val) => { resource.earnings_subtitle = val }"
                                text-type="span"
                                :custom-classes="'text-base opacity-50 ' + getPageContentTextColorClass"
                            />
                        </div>

                        <!-- Compact tier cards -->
                        <div :class="[
                            'grid gap-3',
                            pricingTiers.length === 2 ? 'grid-cols-1 md:grid-cols-2' :
                            'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'
                        ]">
                            <div
                                v-for="(tier, index) in pricingTiers"
                                :key="index"
                                :class="['border p-4 flex items-center justify-between gap-4', borderColorClass, corners]"
                            >
                                <div class="min-w-0">
                                    <div :class="['text-sm font-semibold', getPageContentTextColorClass]">{{ tier.name }}</div>
                                    <div :class="['text-xs opacity-60 mt-0.5', getPageContentTextColorClass]">
                                        {{ formatCurrency(tier.monthly_price) }}/{{ translations.portal_translation_earnings_month ?? 'mo' }}
                                        <span v-if="tier.yearly_price"> · {{ formatCurrency(tier.yearly_price) }}/{{ translations.portal_translation_earnings_year ?? 'yr' }}</span>
                                    </div>
                                </div>
                                <div class="text-right shrink-0">
                                    <p :class="['text-2xl font-bold tracking-tight', getPageContentTextColorClass]">
                                        <span v-if="isRecurring">{{ formatCurrency(calculateEarnings(tier.monthly_price)) }}<span class="text-base opacity-50">/{{ translations.portal_translation_earnings_month ?? 'mo' }}</span></span>
                                        <span v-else>{{ formatCurrency(calculateEarnings(tier.monthly_price)) }}</span>
                                    </p>
                                    <p v-if="tier.yearly_price" :class="['text-sm opacity-50 mt-0.5', getPageContentTextColorClass]">
                                        {{ translations.portal_translation_earnings_or ?? 'or' }}
                                        <span class="font-semibold">
                                            <span v-if="isRecurring">{{ formatCurrency(calculateEarnings(tier.yearly_price)) }}/{{ translations.portal_translation_earnings_year ?? 'yr' }}</span>
                                            <span v-else>{{ formatCurrency(calculateEarnings(tier.yearly_price)) }}</span>
                                        </span>
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Slider card -->
                        <div :class="['bg-black bg-opacity-5 p-5', corners]">
                            <div class="flex items-center justify-between mb-3">
                                <div :class="['text-sm font-semibold', getPageContentTextColorClass]">
                                    {{ referredCustomers.toLocaleString() }}
                                    <span class="font-normal opacity-80">
                                        <span v-if="isRecurring">{{ translations.portal_translation_earnings_referred_customers ?? 'referred customers' }}</span>
                                        <span v-else>{{ translations.portal_translation_earnings_customer_sales ?? 'customer sales' }}</span>
                                    </span>
                                </div>
                                <div v-if="commissionSummaryText" :class="['text-sm opacity-60 shrink-0', getPageContentTextColorClass]">
                                    {{ translations.portal_translation_earnings_earn ?? 'Earn' }} <span class="font-semibold">{{ commissionSummaryText.value }}</span> {{ commissionSummaryText.label }} <span v-if="commissionSummaryText.period" class="font-semibold">{{ commissionSummaryText.period }}</span>
                                </div>
                            </div>
                            <div class="relative">
                                <input
                                    type="range"
                                    v-model.number="referredCustomers"
                                    :min="sliderMin"
                                    :max="sliderMax"
                                    :class="['earnings-slider w-full', getProgressBarClass]"
                                    :style="getProgressBarStyle"
                                />
                                <div :class="['flex justify-between text-xs opacity-60 mt-2', getPageContentTextColorClass]">
                                    <span>{{ sliderMin.toLocaleString() }}</span>
                                    <span>{{ sliderMax.toLocaleString() }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Potential Earnings Table -->
                <div v-if="showEarningsTable" :class="[
                    'border overflow-hidden',
                    contentBgColorClass,
                    borderColorClass,
                    corners
                ]">
                    <div :class="['p-5 border-b', borderColorClass]">
                        <h3 :class="['text-lg font-semibold', getPageContentTextColorClass]">{{ translations.portal_translation_earnings_table_title ?? 'Potential earnings table' }}</h3>
                        <p :class="['text-sm opacity-60', getPageContentTextColorClass]">
                            {{ translations.portal_translation_earnings_table_subtitle ?? 'Based on' }}
                            <span v-if="isPercentCommission">{{ commissionRate }}%</span>
                            <span v-else>{{ formatCurrency(commissionRate) }}</span>
                            {{ translations.portal_translation_earnings_commission_rate ?? 'commission rate' }}
                        </p>
                    </div>
                    <div class="overflow-x-auto">
                        <table class="w-full">
                            <thead class="bg-black/5">
                                <!-- Tier name group headers -->
                                <tr :class="['border-b', borderColorClass]">
                                    <th class="px-4 py-2"></th>
                                    <template v-for="(tier, index) in pricingTiers" :key="'group-' + index">
                                        <th
                                            :colspan="tier.yearly_price ? 2 : 1"
                                            :class="['px-4 py-2 text-center text-sm font-semibold border-l', borderColorClass, getPageContentTextColorClass]"
                                        >
                                            {{ tier.name }}
                                        </th>
                                    </template>
                                </tr>
                                <!-- Price sub-headers -->
                                <tr>
                                    <th :class="['px-4 py-3 text-left text-xs font-medium uppercase tracking-wider opacity-60', getPageContentTextColorClass]">
                                        <template v-if="isRecurring">{{ translations.portal_translation_earnings_customers ?? 'Customers' }}</template>
                                        <template v-else>{{ translations.portal_translation_earnings_sales ?? 'Sales' }}</template>
                                    </th>
                                    <template v-for="(tier, index) in pricingTiers" :key="'header-' + index">
                                        <th :class="['px-4 py-3 text-right text-xs font-medium border-l', borderColorClass, getPageContentTextColorClass]">
                                            <div class="uppercase tracking-wider opacity-60">{{ translations.portal_translation_earnings_monthly ?? 'Monthly' }}</div>
                                            <div class="opacity-40 normal-case tracking-normal mt-0.5">{{ formatCurrency(tier.monthly_price) }}/{{ translations.portal_translation_earnings_month ?? 'mo' }}</div>
                                        </th>
                                        <th v-if="tier.yearly_price" :class="['px-4 py-3 text-right text-xs font-medium', getPageContentTextColorClass]">
                                            <div class="uppercase tracking-wider opacity-60">{{ translations.portal_translation_earnings_yearly ?? 'Yearly' }}</div>
                                            <div class="opacity-40 normal-case tracking-normal mt-0.5">{{ formatCurrency(tier.yearly_price) }}/{{ translations.portal_translation_earnings_year ?? 'yr' }}</div>
                                        </th>
                                    </template>
                                </tr>
                            </thead>
                            <tbody :class="['divide-y', borderColorClass]">
                                <tr v-for="customers in earningsTableRows" :key="customers" class="hover:bg-black/5">
                                    <td :class="['px-4 py-3 text-sm font-medium', getPageContentTextColorClass]">{{ customers.toLocaleString() }}</td>
                                    <template v-for="(tier, index) in pricingTiers" :key="'cell-' + index + '-' + customers">
                                        <td :class="['px-4 py-3 text-sm text-right border-l opacity-75', borderColorClass, getPageContentTextColorClass]">{{ formatCurrency(calculateTableEarnings(customers, tier.monthly_price)) }}</td>
                                        <td v-if="tier.yearly_price" :class="['px-4 py-3 text-sm text-right opacity-75', getPageContentTextColorClass]">{{ formatCurrency(calculateTableEarnings(customers, tier.yearly_price)) }}</td>
                                    </template>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Custom range slider styling */
.earnings-slider {
    -webkit-appearance: none;
    appearance: none;
    height: 8px;
    border-radius: 4px;
    background: #e5e7eb;
    outline: none;
    cursor: pointer;
}

.earnings-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--slider-thumb-color, #14b8a6);
    cursor: pointer;
    border: 3px solid white;
    box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
}

.earnings-slider::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--slider-thumb-color, #14b8a6);
    cursor: pointer;
    border: 3px solid white;
    box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
}

/* Color variants for slider track */
.slider-teal {
    background: linear-gradient(to right, #14b8a6 0%, #14b8a6 v-bind(sliderProgress + '%'), #e5e7eb v-bind(sliderProgress + '%'), #e5e7eb 100%);
}

.slider-indigo {
    background: linear-gradient(to right, #4338ca 0%, #4338ca v-bind(sliderProgress + '%'), #e5e7eb v-bind(sliderProgress + '%'), #e5e7eb 100%);
}

.slider-blue {
    background: linear-gradient(to right, #1d4ed8 0%, #1d4ed8 v-bind(sliderProgress + '%'), #e5e7eb v-bind(sliderProgress + '%'), #e5e7eb 100%);
}

.slider-cyan {
    background: linear-gradient(to right, #0e7490 0%, #0e7490 v-bind(sliderProgress + '%'), #e5e7eb v-bind(sliderProgress + '%'), #e5e7eb 100%);
}

.slider-black {
    background: linear-gradient(to right, #111827 0%, #111827 v-bind(sliderProgress + '%'), #e5e7eb v-bind(sliderProgress + '%'), #e5e7eb 100%);
}
</style>
